"use client";

import { useCallback, useRef, useState } from "react";

// ─── Shared play button overlay (thumbnail use) ───────────────────────────────
export const VideoPlayOverlay = () => (
  <div className="pointer-events-none absolute inset-0 flex items-center justify-center bg-black/30">
    <div className="flex h-8 w-8 items-center justify-center rounded-full border-2 border-white">
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="white" className="h-4 w-4">
        <path d="M8 5v14l11-7z" />
      </svg>
    </div>
  </div>
);

// ─── Video thumbnail wrapper (first-frame + overlay, no playback) ─────────────
interface VideoThumbnailProps {
  src: string;
  className?: string;
}

export const VideoThumbnail = ({ src, className }: VideoThumbnailProps) => (
  <div className={`relative h-full w-full overflow-hidden ${className ?? ""}`}>
    <video
      src={src}
      preload="metadata"
      muted
      playsInline
      className="h-full w-full object-cover pointer-events-none"
    />
    <VideoPlayOverlay />
  </div>
);

// ─── Main video player (autoplay on mount, click to pause/resume) ─────────────
interface VideoPlayerProps {
  src: string;
  /** Call this when the video finishes loading so the parent can mark as loaded */
  onLoaded?: () => void;
  /** Size variant: "main" = large carousel, "modal" = viewer modal */
  size?: "main" | "modal";
  /** Whether to start playing immediately */
  autoPlay?: boolean;
}

export const useVideoPlayer = () => {
  const videoRefs = useRef<Record<number, HTMLVideoElement | null>>({});
  const [playingIdx, setPlayingIdx] = useState<number | null>(null);

  const setVideoRef = (idx: number) => (el: HTMLVideoElement | null) => {
    videoRefs.current[idx] = el;
  };

  const playVideo = useCallback((idx: number) => {
    const video = videoRefs.current[idx];
    if (!video) return;
    video.play().catch(() => {});
    setPlayingIdx(idx);
  }, []);

  const pauseAllVideos = useCallback(() => {
    Object.values(videoRefs.current).forEach((v) => {
      if (v && !v.paused) v.pause();
    });
    setPlayingIdx(null);
  }, []);

  const toggleVideo = useCallback(
    (idx: number, e: React.MouseEvent) => {
      e.stopPropagation();
      const video = videoRefs.current[idx];
      if (!video) return;
      if (video.paused) {
        playVideo(idx);
      } else {
        video.pause();
        setPlayingIdx(null);
      }
    },
    [playVideo],
  );

  return { setVideoRef, playVideo, pauseAllVideos, toggleVideo, playingIdx };
};

// ─── Inline video slide (main carousel & modal main view) ────────────────────
interface VideoSlideProps {
  src: string;
  isPlaying: boolean;
  onToggle: (e: React.MouseEvent) => void;
  onLoaded?: () => void;
  objectFit?: "contain" | "cover";
  controls?: boolean;
  videoRef?: (el: HTMLVideoElement | null) => void;
}

export const VideoSlide = ({
  src,
  isPlaying,
  onToggle,
  onLoaded,
  objectFit = "contain",
  controls = false,
  videoRef,
}: VideoSlideProps) => (
  <div className="relative h-full w-full cursor-pointer" onClick={onToggle}>
    <video
      ref={videoRef}
      src={src}
      className="h-full w-full"
      style={{ objectFit }}
      muted
      playsInline
      loop
      preload="auto"
      controls={controls}
      onLoadedData={onLoaded}
    />
    {/* Show play overlay only when paused */}
    {!isPlaying && (
      <div className="pointer-events-none absolute inset-0 flex items-center justify-center bg-black/20">
        <div className="flex h-14 w-14 items-center justify-center rounded-full border-2 border-white">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="white" className="h-7 w-7">
            <path d="M8 5v14l11-7z" />
          </svg>
        </div>
      </div>
    )}
  </div>
);

export default VideoPlayOverlay;
